import type { TextInnerGlow } from '../../types';
import { isValidTextEffectBlendMode } from '../../shared/blendModes';
import { GLOW_CONTOURS } from './textEffectContours';

// Engineering bound for the initial smooth glow; not a Photoshop size limit.
export const INNER_GLOW_MAX_SIZE = 100;
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export function getTextInnerGlow(value?: TextInnerGlow) {
  if (
    !value ||
    value.version !== 1 ||
    value.enabled !== true ||
    !isValidTextEffectBlendMode(value.blendMode) ||
    value.technique !== 'softer' ||
    !['edge', 'center'].includes(value.source) ||
    !GLOW_CONTOURS.includes(value.contour) ||
    value.noise !== 0 ||
    value.jitter !== 0 ||
    value.antiAlias !== false ||
    typeof value.color !== 'string' ||
    !/^#[0-9a-f]{6}$/i.test(value.color) ||
    ![value.opacity, value.size, value.choke, value.range].every(
      (v) => typeof v === 'number' && Number.isFinite(v)
    )
  )
    return null;
  const size = clamp(value.size, 0, INNER_GLOW_MAX_SIZE);
  const opacity = clamp(value.opacity, 0, 100) / 100;
  if (!size || !opacity) return null;
  const choke = clamp(value.choke, 0, 100) / 100;
  return {
    size,
    // Native Softer glow keeps a two-pixel footprint at authored Size 1.
    // Resolve before output/editor scaling, not in the physical-pixel filter.
    footprintSize: Math.max(2, size),
    opacity,
    color: value.color,
    blendMode: value.blendMode,
    source: value.source,
    contour: value.contour,
    erosion: size * choke,
    // Softer/Edge sigma calibrated against isolated NBA 26–27 glow exports.
    // This remains an empirical mapping, not a general Photoshop guarantee.
    blur: (size * (1 - choke)) / 2.4,
    rangeScale: 100 / clamp(value.range, 1, 100),
  };
}
export type InnerGlowRenderSettings = NonNullable<
  ReturnType<typeof getTextInnerGlow>
>;
export function scaleTextInnerGlow(
  glow: InnerGlowRenderSettings,
  scale: number
): InnerGlowRenderSettings {
  return {
    ...glow,
    size: glow.size * scale,
    footprintSize: glow.footprintSize * scale,
    erosion: glow.erosion * scale,
    blur: glow.blur * scale,
  };
}
